import { SelectionType, ThumbnailType } from '../bom/constants';
import { nextgenProductDetailModels, nextgenUiSteps } from '../main/constants';

import { createSelector } from '@reduxjs/toolkit';
import { digitalTemplateType } from '../utils/constants';
import { getAvailableUniqueOptions } from '../bom/selectors';
import { getColorCode } from '../main/selectors';
import { getCurrentCanvasData } from '../canvas/selectors';
import { getLogoEditingStatus } from '../main/selectors';
import { getSelectedTemplate } from '../template/selectors';
import { getTempBackgroundImage } from '../main/selectors';
import { getTempElement } from '../canvas/selectors';
import { logoEditingStatusEnum } from '../utils/constants';
import { nextgenUiStepButtons } from '../main/constants';

export const getIsNextGenUI = (state) => {
  return state.specs.nextGenUI;
};

export const getSaveProjectDisplayState = (state) => {
  return state.nextgenUi.showSaveProjectSucessMessage;
};

export const getImprintGroups = (state) => {
  return state.ui.imprintGroups;
};

export const getImprintGroupRules = createSelector(
  (state) => state.ui.imprintGroups,
  (_, groupName) => groupName,
  (imprintGroups, groupName) => {
    if (groupName) {
      var group = imprintGroups[groupName];
      return group.imprintRules;
    }
  }
);

export const getImprintGroup = createSelector(
  (state) => state.ui.imprintGroups,
  (_, groupName) => groupName,
  (imprintGroups, groupName) => {
    if (groupName) {
      return imprintGroups[groupName];
    }
  }
);

export const getRuleConfirmModal = createSelector(
  (state) => state.nextgenUi,
  (nextGenUi) => {
    return nextGenUi.ruleChangeConfirmModel;
  }
);

export const getNextgenUiStepSettings = createSelector(
  (state) => state.nextgenUi,
  (nextgenUi) => nextgenUi.stepSettings
);

export const getShowPreconfigModel = (state) => {
  const stepSettings = getNextgenUiStepSettings(state);
  return (
    stepSettings.nextgenStep === nextgenUiSteps.ProductDetails &&
    stepSettings.productDetailMode === nextgenProductDetailModels.OnPage
  );
};

export const getNextgenUiCurrentButtonType = (state) => {
  const stepSettings = getNextgenUiStepSettings(state);

  if (!stepSettings.steps || Object.keys(stepSettings.steps).length === 0) {
    return nextgenUiStepButtons.AddToCart;
  }

  return stepSettings.steps[stepSettings.nextgenStep];
};

export const getProductDetailButtonTypeOnPage = (state) => {
  const stepSettings = getNextgenUiStepSettings(state);

  if (stepSettings.productDetailMode === nextgenProductDetailModels.OnPage) {
    return getNextgenUiCurrentButtonType(state);
    // -- nextgenUiStepButtons.Next
    // -- nextgenUiStepButtons.AddToCart
  }

  return undefined;
};

export const getNextgenUiCurrentStep = (state) => {
  const stepSettings = getNextgenUiStepSettings(state);

  return stepSettings.nextgenStep;
};

export const getShowImprintColorsSection = (state) => {
  const colorCodes = getColorCode(state);
  if (colorCodes.length < 1) {
    return false;
  } else if (colorCodes.length === 1) {
    const colorCode = colorCodes[0];
    const questions = state.questions;
    const colorQuestion = questions.find((x) => x.code === colorCode);
    if (!colorQuestion || colorQuestion.selectionType === SelectionType.SINGLE) {
      return false;
    }
  } else {
    const questions = state.questions;
    const hasMultiple = questions.some(
      (x) => colorCodes.indexOf(x.code) > -1 && x.selectionType === SelectionType.MULTIPLE
    );

    if (!hasMultiple) {
      return false;
    }
  }

  const availableOptions = getAvailableUniqueOptions(state);
  if (availableOptions.length <= 1) {
    return false;
  }

  return true;
};

export const getShowSaveProjectModal = (state) => {
  return state.nextgenUi.showSaveProjectModal;
};

export const getShowGetStartedOnCanvas = (state) => {
  const isNextgenUi = getIsNextGenUI(state);
  if (!isNextgenUi) return false;

  const selectedTemplate = getSelectedTemplate(state);
  const hasSelectedTemplate = selectedTemplate && selectedTemplate.templateType !== digitalTemplateType.Blank;
  if (hasSelectedTemplate) return false;

  const canvasData = getCurrentCanvasData(state);
  const textList = canvasData.textList;
  const imageList = canvasData.imageList;

  let elementList = [];
  if (textList) {
    elementList = elementList.concat(textList);
  }
  if (imageList) {
    elementList = elementList.concat(imageList);
  }
  elementList.sort((e1, e2) => e1.data.zIndex - e2.data.zIndex);

  if (elementList.length > 0) return false;

  const tempElement = getTempElement(state);
  const tempBackgroundImage = getTempBackgroundImage(state);
  const logoEditingStatus = getLogoEditingStatus(state);
  const hasTempElement =
    tempElement ||
    (tempBackgroundImage && tempBackgroundImage.data) ||
    logoEditingStatus.currentStatus === logoEditingStatusEnum.DpiWarningPanel ||
    logoEditingStatus.currentStatus === logoEditingStatusEnum.DpiWarningButton
      ? true
      : false;

  return !hasTempElement;
};

export const getCanvasContextMenuActions = (state) => state.nextgenUi.canvasContextMenuActions;
export const getAlertInfo = (state) => state.nextgenUi.alertInfo;

export const getBackupTextColor = (state, textGuid) => {
  return state.nextgenUi.backupTextColor[textGuid];
};

export const getBackupTextOutlineColor = (state, textGuid) => {
  return state.nextgenUi.backupTextOutlineColor[textGuid];
};

export const getIsRequestiongPreview = (state) => {
  return state.nextgenUi.isRequestingPreview;
};

export const getSelectedImageBackupColor = (state, guid) => {
  const backupImages = state.nextgenUi.backupElements.image;
  const colors = backupImages.find((item) => item.ui.guid === guid)?.data.colors;
  return colors;
};

export const getSelectedTextBackupColor = (state, guid) => {
  const backupTexts = state.nextgenUi.backupElements.text;
  const color = backupTexts.find((item) => item.ui.guid === guid)?.data.color;
  return color;
};

export const getCustomPMSColors = (state) => {
  return state.nextgenUi.customPMSColors;
};

export const getBackupColorCharge = (state) => state.nextgenUi.backupColorCharge;

export const getFreeColor = (state) => {
  return state.nextgenUi.freeColor;
};

export const getIsStackPanelShow = (state) => {
  const stackPanelChain = state.nextgenUi.flyoutPanel.stackPanelChain;
  return stackPanelChain && stackPanelChain.length > 0 && stackPanelChain[0].length > 1;
};
